import React, {useState} from 'react';
import {t} from 'i18next';
import {Link} from 'react-router';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faLink} from '@fortawesome/free-solid-svg-icons/faLink';
import {CollectionProgress as CollectionProgressEntry} from '@types';
import {useFetchCollectionProgressQuery} from '@goproblems/shared/api/indexPage';
import CollectionProgressCardSkeleton from '@components/skeleton/CollectionProgressCardSkeleton';
import generateColor from '@components/levelProgressBar/generateColor';
import {ThemeAwareSkeleton} from '@components/theme/ThemeAwareSkeleton';
import useNumberOfItemsPerRow from '@utils/hooks/useNumberOfItemsPerRow';
import IndexSectionHeading from './IndexSectionHeading';

const CollectionProgressCard: React.FC<{
    collectionProgressItem: CollectionProgressEntry;
}> = ({collectionProgressItem}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const progress =
        (collectionProgressItem.numberOfSolvedProblems /
            collectionProgressItem.numberOfProblems) *
        100;
    const speedProgress =
        (collectionProgressItem.numberOfSpeedSolvedProblems /
            collectionProgressItem.numberOfProblems) *
        100;
    return (
        <div className="card relative h-100">
            <span className="absolute inset-x-0 top-0 z-10">
                <Link
                    to={`/collections/${collectionProgressItem.id}`}
                    aria-label={`Open ${collectionProgressItem.name} collection`}
                    className="text-decoration-none">
                    <span className="mt-1 ml-1 inline-flex size-6 items-center justify-center rounded-full bg-white text-xs text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-neutral-400">
                        <FontAwesomeIcon icon={faLink} />
                    </span>
                </Link>
            </span>
            <Link
                to={`/problems/${collectionProgressItem.previewProblem.id}?name=collection&collectionId=${collectionProgressItem.id}&offset=0`}
                className="text-decoration-none h-100">
                {!imageLoaded && (
                    <ThemeAwareSkeleton
                        style={{
                            width: '100%',
                            aspectRatio: 1.02, // compensate for the size gap between the skeleton and the image
                        }}
                    />
                )}
                <img
                    onLoad={() => setImageLoaded(true)}
                    className="card-img-top"
                    style={{display: imageLoaded ? 'block' : 'none'}}
                    src={collectionProgressItem.previewProblem.imageUrl}
                    alt={collectionProgressItem.name}
                />
            </Link>
            <Link
                to={`/collections/${collectionProgressItem.id}`}
                className="text-decoration-none">
                <div className="card-body p-2 flex justify-content-between flex-column">
                    <span
                        title={collectionProgressItem.name}
                        className="text-two-lines">
                        {collectionProgressItem.name}
                    </span>
                    <div className="mt-1 flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <div
                                className={`progress progress-bar-collections progress-height-5 ${progress === 100 ? 'green-border' : ''}`}
                                style={{position: 'relative'}}>
                                <div
                                    role="progressbar"
                                    aria-label={`${collectionProgressItem.name} collection progress`}
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor:
                                            generateColor(progress),
                                    }}
                                    aria-valuenow={progress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}></div>
                                {speedProgress > 0 && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            width: `${speedProgress}%`,
                                            backgroundColor: '#339af0',
                                            height: '100%',
                                            zIndex: 1,
                                        }}
                                        title={`${t('collections_browser.speed_solves')}: ${collectionProgressItem.numberOfSpeedSolvedProblems} (${speedProgress.toFixed(1)}%)`}
                                    />
                                )}
                            </div>
                        </div>
                        <span
                            className="shrink-0"
                            title={t('index.problems_in_this_collection')}>
                            {collectionProgressItem.numberOfProblems}
                        </span>
                    </div>
                </div>
            </Link>
        </div>
    );
};

const CollectionProgress: React.FC = () => {
    const {
        data: collectionProgressEntries,
        isLoading,
        isError,
    } = useFetchCollectionProgressQuery(10);
    const numberOfItems = useNumberOfItemsPerRow();

    // Determine the number of placeholder items needed
    const placeholders = collectionProgressEntries
        ? Math.max(0, numberOfItems - collectionProgressEntries.length)
        : 0;

    // Generate visibility classes dynamically for grid layout
    const getVisibilityClasses = (index: number): string => {
        if (index >= 8) return 'hidden site-xxl:block';
        if (index >= 6) return 'hidden site-xl:block';
        if (index >= 4) return 'hidden md:block';
        if (index >= 2) return 'hidden site-sm:block';
        return '';
    };

    if (isError || collectionProgressEntries?.length === 0) {
        return (
            <div>{t('index.section_load_error')}</div>
        );
    }

    return (
        <div className="index-home-feature index-home-card-list w-full">
            <IndexSectionHeading
                title={t('index.collections_progress')}
                to="/collections/progress"
            />
            <div className="flex flex-nowrap gap-2">
                {isLoading
                    ? Array.from(
                          {length: numberOfItems},
                          (_, index) => (
                              <div
                                  key={`skeleton-${index}`}
                                  className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}>
                                  <CollectionProgressCardSkeleton />
                              </div>
                          )
                      )
                    : collectionProgressEntries
                          ?.slice(0, numberOfItems)
                          .map((item, index) => (
                              <div
                                  key={item.id}
                                  className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}>
                                  <CollectionProgressCard
                                      collectionProgressItem={item}
                                  />
                              </div>
                          ))}
                {!isLoading &&
                    Array.from({length: placeholders}, (_, index) => (
                        <div
                            key={`placeholder-${index}`}
                            className={`min-w-0 flex-1 ${getVisibilityClasses(index)}`}
                        />
                    ))}
            </div>
        </div>
    );
};

export default CollectionProgress;
